import { useEffect } from 'react';

import { Button } from '@mui/material';
import Container from '@mui/material/Container';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { getPackagingItemById } from 'src/redux/slices/packaging-item';

import ErrorContent from 'src/components/error-content';
import { useSettingsContext } from 'src/components/settings';
import { LeftIcon } from 'src/components/carousel/arrow-icons';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';

import PackagingItemDetails from '../packaging-item-details';
import { LoadingScreen } from '../../../components/loading-screen';
import { useAppDispatcher, useAppSelector } from '../../../redux/store';

type Props = {
  id: string;
};

export default function PackagingItemDetailView({ id }: Props) {
  const { item, status, error } = useAppSelector((state) => state.packagingItem);

  const settings = useSettingsContext();
  const dispatch = useAppDispatcher();

  useEffect(() => {
    dispatch(getPackagingItemById({ itemID: id }));
  }, [dispatch, id]);

  if (status === 'loading' || status === 'idle') {
    return <LoadingScreen />;
  }

  if (status === 'failure') {
    return (
      <ErrorContent
        filled
        title="Failed to load packaging item details"
        description={error}
        action={
          <Button
            component={RouterLink}
            href={paths.dashboard.packagingItem.root}
            variant="contained"
            startIcon={<LeftIcon />}
          >
            Back to Packaging Items
          </Button>
        }
        sx={{
          py: 10,
        }}
      />
    );
  }

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Packaging Item Details"
        links={[
          {
            name: 'Packaging Items',
            href: paths.dashboard.packagingItem.root,
          },
          { name: 'Packaging Item Details' },
        ]}
        sx={{
          mb: { xs: 3, md: 5 },
        }}
      />

      <PackagingItemDetails currentPackagingItem={item} />
    </Container>
  );
}
